import { CANCELED_BY_USER } from "@modules/common/types/errorTypes"
import { useServicesContext } from "@modules/core/services/services.context"
import { word } from "@modules/core/utils/i18n"
import { useStripePayment } from "@modules/finances/hooks/useStripeAccount"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useToast } from "@modules/ui/components/huToast"
import { LoggerType } from "@modules/utils/loggerUtils"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

export const useBoxOfferSubscription = () => {
  const [isProcessing, setIsProcessing] = useState(false)

  const { boxOfferApi } = useServicesContext()
  const toast = useToast()
  const { stripePayment } = useStripePayment()

  const subscribeToFreeOffer = useMutation({
    mutationFn: async ({ boxId, offerId }: { boxId: string; offerId: string }) => {
      return await boxOfferApi.subscribeFree(boxId, offerId)
    },
    onSuccess: () => {
      toast.show({
        summary: "Subscription successful",
      })
    },
  })

  const subscribeToPayingOffer = useMutation({
    mutationFn: async ({ offerId }: { offerId: string }) => {
      const currentUrl = window.location.href
      return await boxOfferApi.createBoxOfferPaymentSubscription(offerId, currentUrl, currentUrl)
    },
    onSuccess: async (data) => {
      try {
        setIsProcessing(true)
        const success = await stripePayment(data.url)
        if (success) {
          toast.show({
            detail: word("program.subscribe.success.toast"),
            severity: "success",
          })
        }
      } catch (e: any) {
        if (e !== CANCELED_BY_USER) {
          if (e.code === "PROGRAM_HAS_NO_PRICE") {
            console.warn(LoggerType.Program + "try to paid a free offer ...")
          } else {
            console.error(LoggerType.Stripe + "subscribe on paid offer", e)
            toast.show({
              detail: word("program.subscribe.error.toast"),
              severity: "error",
            })
          }
        }
        return false
      } finally {
        setIsProcessing(false)
      }
    },
    onError: () => {
      toast.show({
        severity: "error",
        detail: word("program.subscribe.error.toast"),
      })
    },
  })

  const subscribeToOffer = async (boxId: string, offerId: string, isFree: boolean) => {
    if (isFree) {
      subscribeToFreeOffer.mutateAsync({ boxId, offerId })
    } else {
      subscribeToPayingOffer.mutateAsync({ offerId })
    }
  }

  return {
    subscribeToOffer,
    isProcessing: isProcessing || subscribeToFreeOffer.isPending || subscribeToPayingOffer.isPending,
  }
}

export const useBoxOfferInvitationByUser = () => {
  const { boxOfferApi } = useServicesContext()
  const toast = useToast()
  const { hideSidebar } = useSidebar()
  const queryClient = useQueryClient()

  const refuseOfferInvitation = useMutation({
    mutationFn: async ({ boxOfferInvitationId }: { boxOfferInvitationId: string }) => {
      return await boxOfferApi.refuseOfferInvitation(boxOfferInvitationId)
    },
    onSuccess: (data) => {
      console.log("data", data)
      toast.show({
        severity: "success",
        summary: "Invitation refused",
      })

      queryClient.invalidateQueries({
        queryKey: ["boxOfferUser", data.boxOfferId, data.boxId],
      })

      hideSidebar()
    },
    onError: () => {
      toast.show({
        severity: "error",
        detail: word("box.staff.detailsPending.toast.error"),
      })
    },
  })

  return {
    refuseOfferInvitation,
  }
}
